import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorOutline, RefreshOutlined } from '@mui/icons-material';

import { Shimmer } from '../../atome/motion/motion.styled';
import { OrderServices } from '../../../api/order';
import {
  AdminOrder,
  ORDER_STATUSES,
  OrderStatus,
  PaymentProvider,
} from '../../models/order/order';
import {
  CountPill,
  ErrorBanner,
  FilterBar,
  FilterField,
  GhostButton,
  ItemMeta,
  ItemName,
  ItemRow,
  ItemsBlock,
  ItemsHead,
  ItemTotal,
  OrderCard,
  OrderField,
  OrderGrid,
  OrderList,
  OrderTop,
  OrderTotal,
  Pager,
  Panel,
  Select,
  StatusSelect,
  Toolbar,
} from './admin.styled';

const STATUS_TONE: Record<OrderStatus, 'forest' | 'warning' | 'danger' | 'sand'> = {
  paid: 'forest',
  done: 'forest',
  pending: 'warning',
  failed: 'danger',
  cancelled: 'sand',
};

const PAYMENT_LABEL: Record<PaymentProvider, string> = {
  delivery: 'Cash on delivery',
  bank: 'Bank transfer',
  wishmoney: 'Whish Money',
};

/** 0 means "no limit" — the API returns every order when limit is omitted. */
const PAGE_SIZES = [0, 10, 25, 50];

const money = (value: number) => `$${value.toFixed(2)}`;

/** MySQL hands back "2026-08-17 14:32:05"; Safari refuses that without the T. */
const formatDate = (value: string) => {
  if (!value) return '—';
  const parsed = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Every order placed on the shop, newest first.
 *
 * GET /api/orders joins each order with its address and payment and nests its
 * line items, so a card shows the whole order without a second call. The status
 * picker on each card writes through PATCH /api/orders/:id/status.
 */
const Orders = () => {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [pageSize, setPageSize] = useState(0);
  const [page, setPage] = useState(0);

  /** Order whose status is mid-flight, so its picker can lock. */
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setOrders(
        await OrderServices.getOrders({
          status: status || undefined,
          // Both are left off entirely when showing everything; the API rejects
          // an offset that arrives without a limit.
          limit: pageSize || undefined,
          offset: pageSize ? page * pageSize : undefined,
        })
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [status, pageSize, page]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatusFilter = (value: OrderStatus | '') => {
    setStatus(value);
    // A narrower list makes the current page number meaningless.
    setPage(0);
  };

  const changePageSize = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  /**
   * The PATCH answers with the order row, which carries no line items — so only
   * the status is written back into the row already on screen. That repaint is
   * just for the wait: the list is re-read straight after, because with a status
   * filter on, the order this admin just edited may no longer belong in the view.
   */
  const changeOrderStatus = async (order: AdminOrder, next: OrderStatus) => {
    if (next === order.status) return;
    setError('');
    setSavingId(order.id);
    try {
      const saved = await OrderServices.updateOrderStatus(order.id, next);
      setOrders((current) =>
        current.map((row) => (row.id === order.id ? { ...row, status: saved } : row))
      );
    } catch (err: any) {
      setError(err?.message || 'Could not update the order status');
      // Nothing moved on the server, so there is nothing to re-read.
      return;
    } finally {
      setSavingId(null);
    }

    await load();
  };

  const filters = (
    <FilterBar>
      <FilterField>
        Status
        <Select
          value={status}
          disabled={loading}
          onChange={(e) => changeStatusFilter(e.target.value as OrderStatus | '')}
        >
          <option value="">All statuses</option>
          {ORDER_STATUSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
      </FilterField>

      <FilterField>
        Per page
        <Select
          value={pageSize}
          disabled={loading}
          onChange={(e) => changePageSize(Number(e.target.value))}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size === 0 ? 'Show all' : size}
            </option>
          ))}
        </Select>
      </FilterField>
    </FilterBar>
  );

  if (loading && orders.length === 0) {
    return (
      <OrderList>
        {[0, 1, 2].map((i) => (
          <OrderCard key={i}>
            <Shimmer $height="22px" />
            <Shimmer $height="14px" />
            <Shimmer $height="14px" />
          </OrderCard>
        ))}
      </OrderList>
    );
  }

  if (error && orders.length === 0) {
    return (
      <Panel>
        <h3>We could not load your orders</h3>
        <p>{error}</p>
        <GhostButton type="button" onClick={load}>
          <RefreshOutlined sx={{ fontSize: 17 }} />
          Try again
        </GhostButton>
      </Panel>
    );
  }

  // An empty page is only a dead end when there is nothing to go back to — with
  // a filter or a page turn in play the controls have to stay reachable.
  if (orders.length === 0) {
    const narrowed = Boolean(status) || page > 0;

    return (
      <>
        {narrowed && <Toolbar>{filters}</Toolbar>}

        <Panel>
          <h3>{narrowed ? 'Nothing here' : 'No orders yet'}</h3>
          <p>
            {narrowed
              ? 'No orders match this view. Try another status, or step back a page.'
              : 'Every order placed on the shop will land here, newest first.'}
          </p>
          {narrowed && (
            <GhostButton
              type="button"
              onClick={() => {
                setStatus('');
                setPage(0);
              }}
            >
              Show all orders
            </GhostButton>
          )}
        </Panel>
      </>
    );
  }

  // The API returns no total count, so a full page is the only hint that there
  // may be another one behind it.
  const hasNextPage = pageSize > 0 && orders.length === pageSize;

  return (
    <>
      {error && (
        <ErrorBanner role="alert">
          <ErrorOutline fontSize="small" />
          {error}
        </ErrorBanner>
      )}

      <Toolbar>
        {filters}

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <CountPill>
            {orders.length} {orders.length === 1 ? 'order' : 'orders'}
            {pageSize > 0 && page > 0 ? ` · page ${page + 1}` : ''}
          </CountPill>

          <GhostButton type="button" onClick={load} disabled={loading}>
            <RefreshOutlined sx={{ fontSize: 17 }} />
            Refresh
          </GhostButton>
        </div>
      </Toolbar>

      <OrderList>
        {orders.map((order) => {
          const saved = order.subtotal - order.grandTotal;

          return (
            <OrderCard key={order.id}>
              <OrderTop>
                <div>
                  <h3>Order #{order.id}</h3>
                  <time dateTime={order.createdAt}>{formatDate(order.createdAt)}</time>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                  <StatusSelect
                    aria-label={`Status of order ${order.id}`}
                    $tone={STATUS_TONE[order.status] || 'sand'}
                    value={order.status}
                    disabled={savingId === order.id}
                    onChange={(e) => changeOrderStatus(order, e.target.value as OrderStatus)}
                  >
                    {ORDER_STATUSES.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </StatusSelect>

                  <OrderTotal>
                    <strong>{money(order.grandTotal)}</strong>
                    {saved > 0.005 && <del>{money(order.subtotal)}</del>}
                  </OrderTotal>
                </div>
              </OrderTop>

              <OrderGrid>
                <OrderField>
                  <span>Customer</span>
                  <p>
                    {order.firstName} {order.lastName}
                  </p>
                  <p>
                    <a href={`tel:${order.phoneNumber}`}>{order.phoneNumber}</a>
                  </p>
                  {order.customerEmail ? (
                    <p>
                      <a href={`mailto:${order.customerEmail}`}>{order.customerEmail}</a>
                    </p>
                  ) : (
                    <p style={{ opacity: 0.6 }}>Guest checkout</p>
                  )}
                </OrderField>

                <OrderField>
                  <span>Deliver to</span>
                  <p>
                    {[order.city, order.state].filter(Boolean).join(', ') || '—'}
                  </p>
                  <p>{order.addressDescription}</p>
                </OrderField>

                <OrderField>
                  <span>Payment</span>
                  <p>
                    {order.paymentProvider
                      ? PAYMENT_LABEL[order.paymentProvider] || order.paymentProvider
                      : '—'}
                  </p>
                  <p style={{ color: '#6d6861' }}>
                    {order.paymentStatus || '—'}
                    {order.paymentId ? ` · ${order.paymentId}` : ''}
                  </p>
                </OrderField>
              </OrderGrid>

              {order.items.length > 0 && (
                <ItemsBlock>
                  <ItemsHead>
                    <span>Items</span>
                    <span>
                      {order.totalQuantity} {order.totalQuantity === 1 ? 'unit' : 'units'}
                    </span>
                  </ItemsHead>

                  {order.items.map((item) => (
                    <ItemRow key={item.id}>
                      <ItemName>
                        {/* The name is a checkout-time snapshot, so it renders
                            whether or not the product still exists. */}
                        {item.productId === null ? (
                          <>
                            {item.productName}
                            <small>No longer in the catalogue</small>
                          </>
                        ) : (
                          <Link to={`/product/${item.productId}`}>{item.productName}</Link>
                        )}
                      </ItemName>

                      <ItemMeta>
                        {item.quantity} × {money(item.unitPrice)}
                      </ItemMeta>

                      <ItemTotal>{money(item.lineTotal)}</ItemTotal>
                    </ItemRow>
                  ))}
                </ItemsBlock>
              )}
            </OrderCard>
          );
        })}
      </OrderList>

      {(page > 0 || hasNextPage) && (
        <Pager>
          <GhostButton
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={loading || page === 0}
          >
            Previous
          </GhostButton>
          <span>Page {page + 1}</span>
          <GhostButton
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={loading || !hasNextPage}
          >
            Next
          </GhostButton>
        </Pager>
      )}
    </>
  );
};

export default Orders;
